import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Music, ShoppingBag, User, Menu, X, LogOut, Video, Radio, Crown, Megaphone, Coins, Disc3 } from 'lucide-react';
import CreditBalanceBadge from './CreditBalanceBadge';
import ShopifyCartToggle from './ShopifyCartToggle';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useBeatsCart } from '../contexts/BeatsCartContext';

const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] border-2 border-[#D4AF37] flex items-center justify-center">
      <div className="w-5 h-6 bg-[#D4AF37]" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
    </div>
    <span className="hidden md:block font-['Bebas_Neue'] text-xl tracking-wider">YOUNG BLACK MAN INC.</span>
  </Link>
);

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { toggleCart, itemCount } = useCart();
  const { toggleCart: toggleBeatsCart, itemCount: beatsCount } = useBeatsCart();
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/music', label: 'Music', icon: Music },
    { path: '/albums', label: 'Albums', icon: Disc3 },
    { path: '/beats', label: 'Beats', icon: Radio },
    { path: '/bulletin', label: 'Bulletin', icon: ShoppingBag },
    { path: '/videos', label: 'Videos', icon: Video },
    { path: '/downloads', label: 'PDF Books & More', icon: ShoppingBag },
    { path: '/subscription', label: 'Subscribe', icon: Crown },
    { path: '/advertise', label: 'Advertise With Us', icon: Megaphone },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-11 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo />
          
          {/* Desktop Navigation — show on lg+ so the auth button always fits at md */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`text-sm uppercase tracking-[0.2em] font-medium transition-colors ${
                  isActive(path) ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-white'
                }`}
                data-testid={`nav-${label.toLowerCase()}`}
              >
                {label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                className={`text-sm uppercase tracking-[0.2em] font-medium transition-colors ${
                  location.pathname.startsWith('/admin') ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-white'
                }`}
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3 md:gap-4">
            {/* Music Cart (Shopify) — persistent toggle + mini-cart for singles */}
            <ShopifyCartToggle className="hidden sm:block" />

            {/* Beats Cart */}
            <button
              onClick={toggleBeatsCart}
              className="relative p-2 text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="beats-cart-button"
              title="Beats Cart"
            >
              <Radio size={20} />
              {beatsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-black text-xs font-bold rounded-full flex items-center justify-center">
                  {beatsCount}
                </span>
              )}
            </button>

            <button
              onClick={toggleCart}
              className="relative p-2 text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="cart-button"
              title="Merch Cart"
            >
              <ShoppingBag size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-black text-xs font-bold rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </button>

            <CreditBalanceBadge />

            {user ? (
              <div className="flex items-center gap-2 md:gap-3">
                <Link
                  to="/account"
                  className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white transition-colors"
                  data-testid="account-link"
                  title={user.name || 'Account'}
                >
                  <User size={18} />
                  <span className="hidden lg:inline">{user.name}</span>
                </Link>
                <button
                  onClick={logout}
                  className="p-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                  data-testid="logout-button"
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="px-3 md:px-4 py-2 bg-[#D4AF37] text-black font-medium text-xs md:text-sm uppercase tracking-wider hover:bg-[#E5C048] transition-colors whitespace-nowrap"
                data-testid="login-link"
              >
                Sign In
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-[#0D0D0D] border-t border-white/10">
          <div className="px-4 py-4 space-y-4">
            {navLinks.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center gap-3 py-2 ${
                  isActive(path) ? 'text-[#D4AF37]' : 'text-white'
                }`}
              >
                <Icon size={20} />
                <span className="uppercase tracking-wider">{label}</span>
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 py-2 text-white"
              >
                <User size={20} />
                <span className="uppercase tracking-wider">Admin</span>
              </Link>
            )}
            {user ? (
              <>
                <Link
                  to="/account"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-white"
                >
                  <User size={20} />
                  <span>Account</span>
                </Link>
                <button
                  onClick={() => { logout(); setIsMenuOpen(false); }}
                  className="flex items-center gap-3 py-2 text-[#D4AF37]"
                >
                  <LogOut size={20} />
                  <span>Sign Out</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full py-3 bg-[#D4AF37] text-black font-medium text-center uppercase tracking-wider"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
